import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { useParamState } from '@app/util/hooks/useParamState'
import { HousingFormPageOverview } from './sections/HousingFormPageOverview'
import { HousingFormPagePlots } from './sections/HousingFormPagePlots'
import { HousingFormPageInitiatives } from './sections/HousingFormPageInitiatives'
import { housingFormTabs, housingFormTabsQueryParamName } from './constants'
import { Thread } from '@app/components/blocks/thread/Thread'
import { ViewHousingFormModel } from 'functions/app/housing-form/view'
import useMediaQuery from '@app/util/useMediaQuery'
import { HousingFormPagePopularHousingForms } from './sections/HousingFormPagePopularHousingForms'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { ArticleSection } from '@app/components/blocks/ArticleSection'
import { HOUSING_FORM_FEATURES } from '@schema/featureToggle'
import { isFeatureAvailable } from '@app/util/featureToggle'
import { HousingFormPageExperts } from './sections/HousingFormPageExperts'

export const TabContent = ({
  housingForm,
}: {
  housingForm: ViewHousingFormModel
}) => {
  const t = useTranslations().app.housingFormPage.tabs
  const isMobile = useMediaQuery()
  const showKnowledgeHubTab = isFeatureAvailable(
    housingForm.featureToggles,
    HOUSING_FORM_FEATURES.knowledgeHub,
    [housingForm.knowledgeHubModules],
  )

  const showCurrentTab = isFeatureAvailable(
    housingForm.featureToggles,
    HOUSING_FORM_FEATURES.current,
    [housingForm.stories, housingForm.news, housingForm.blogs],
  )
  const showExpertsTab = isFeatureAvailable(
    housingForm.featureToggles,
    HOUSING_FORM_FEATURES.experts,
    [housingForm.serviceProviders],
  )

  const [tab, setTab] = useParamState(
    housingFormTabs,
    housingFormTabsQueryParamName,
  )

  return (
    <Column alignItems="stretch" gap="2rem" style={{ width: '100%' }}>
      <TabsGroup
        selectedTab={tab}
        onSelectTab={setTab}
        tabs={[
          { label: t.overview, value: housingFormTabs.overview },
          { label: t.discussion, value: housingFormTabs.discussion },
          { label: t.initiatives, value: housingFormTabs.initiatives },
          { label: t.plots, value: housingFormTabs.plots },
          {
            label: t.knowledgeHub,
            value: housingFormTabs.knowledgeHub,
            isVisible: showKnowledgeHubTab,
          },
          {
            label: t.current,
            value: housingFormTabs.current,
            isVisible: showCurrentTab,
          },
          {
            label: t.experts,
            value: housingFormTabs.experts,
            isVisible: showExpertsTab,
          },
          {
            label: t.popularHousingForms,
            value: housingFormTabs.popularHousingForms,
            isVisible: isMobile,
          },
        ]}
      />

      {match(tab)
        .with(housingFormTabs.overview, () => (
          <HousingFormPageOverview housingForm={housingForm} />
        ))
        .with(housingFormTabs.discussion, () =>
          housingForm.thread ? (
            <Thread key="thread" threadId={housingForm.thread.id} />
          ) : null,
        )
        .with(housingFormTabs.initiatives, () => (
          <HousingFormPageInitiatives housingForm={housingForm} />
        ))
        .with(housingFormTabs.plots, () => (
          <HousingFormPagePlots housingForm={housingForm} />
        ))
        .with(housingFormTabs.experts, () =>
          showExpertsTab ? (
            <HousingFormPageExperts housingForm={housingForm} />
          ) : null,
        )
        .with(housingFormTabs.knowledgeHub, () =>
          showKnowledgeHubTab ? (
            <KnowledgeHubModulesSection
              modules={housingForm.knowledgeHubModules ?? []}
            />
          ) : null,
        )
        .with(housingFormTabs.current, () =>
          showCurrentTab ? (
            <ArticleSection
              stories={housingForm.stories ?? []}
              news={housingForm.news ?? []}
              blogs={housingForm.blogs ?? []}
            />
          ) : null,
        )
        .with(housingFormTabs.popularHousingForms, () => (
          <HousingFormPagePopularHousingForms excludeIds={[housingForm.id]} />
        ))
        .exhaustive()}
    </Column>
  )
}
